/**
 * paradisesociety 3D header logo
 * Desktop (fine pointer): camera tilts toward cursor.
 * Mobile / coarse: finger drag (inverted axes, full range); after ~1.2s idle, ease back to default.
 */
(function () {
  'use strict';

  var SELECTOR = 'model-viewer.header__heading-logo-3d';
  var FINE = '(hover: hover) and (pointer: fine)';
  var MAX_YAW = 22;
  var MAX_PITCH = 12;
  var BASE_PHI = 75;
  var DECAY = 60;
  var DECAY_MOBILE = 40;
  var DECAY_MOBILE_RESET = 80;
  var DRAG_THRESHOLD = 8;
  /* Pitch kept in model-viewer-safe band (0–180); yaw unlimited */
  var PHI_MIN = 5;
  var PHI_MAX = 175;
  /* After finger lift, wait then ease back to default orbit */
  var RESET_DELAY_MS = 400;

  var existing = window.__fkxGlbLogoTrack;
  if (existing && typeof existing.destroy === 'function') {
    existing.destroy();
  }

  /** @type {AbortController|null} */
  var controller = null;
  /** @type {HTMLElement|null} */
  var viewer = null;
  var raf = 0;
  var pendingX = 0;
  var pendingY = 0;
  var hasPending = false;
  var isDragging = false;
  var dragMoved = false;
  var dragStartX = 0;
  var dragStartY = 0;
  var orbitYaw = 0;
  var orbitPhi = BASE_PHI;
  var dragBaseYaw = 0;
  var dragBasePhi = BASE_PHI;
  var resetTimer = 0;

  function isDesktop() {
    return window.matchMedia(FINE).matches;
  }

  function clearResetTimer() {
    if (resetTimer) {
      window.clearTimeout(resetTimer);
      resetTimer = 0;
    }
  }

  function setOrbit(yaw, phi) {
    if (!viewer) return;
    orbitYaw = yaw;
    orbitPhi = phi;
    viewer.cameraOrbit =
      yaw.toFixed(2) + 'deg ' + phi.toFixed(2) + 'deg auto';
  }

  function resetOrbit() {
    if (!viewer || isDragging) return;
    try {
      viewer.interpolationDecay = DECAY_MOBILE_RESET;
    } catch (e) {}
    setOrbit(0, BASE_PHI);
  }

  function scheduleReset() {
    clearResetTimer();
    resetTimer = window.setTimeout(function () {
      resetTimer = 0;
      resetOrbit();
    }, RESET_DELAY_MS);
  }

  function applyCursorOrbit(clientX, clientY) {
    if (!viewer) return;
    var rect = viewer.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var cx = rect.left + rect.width / 2;
    var cy = rect.top + rect.height / 2;
    var nx = (clientX - cx) / Math.max(window.innerWidth * 0.45, 1);
    var ny = (clientY - cy) / Math.max(window.innerHeight * 0.45, 1);
    nx = Math.max(-1, Math.min(1, nx));
    ny = Math.max(-1, Math.min(1, ny));

    var yaw = -nx * MAX_YAW;
    var phi = BASE_PHI - ny * MAX_PITCH;
    setOrbit(yaw, phi);
  }

  function onPointerMove(event) {
    pendingX = event.clientX;
    pendingY = event.clientY;
    hasPending = true;
    if (raf) return;
    raf = window.requestAnimationFrame(function () {
      raf = 0;
      if (!hasPending) return;
      hasPending = false;
      applyCursorOrbit(pendingX, pendingY);
    });
  }

  function bindDesktop(mv) {
    viewer = mv;
    mv.removeAttribute('auto-rotate');
    mv.removeAttribute('camera-controls');
    try {
      mv.autoRotate = false;
    } catch (e) {}
    mv.interpolationDecay = DECAY;
    if (!mv.getAttribute('camera-orbit')) {
      setOrbit(0, BASE_PHI);
    }
  }

  function bindMobile(mv) {
    viewer = mv;
    mv.removeAttribute('camera-controls');
    mv.removeAttribute('auto-rotate');
    mv.removeAttribute('rotation-per-second');
    mv.removeAttribute('disable-tap');
    try {
      mv.autoRotate = false;
    } catch (e) {}
    mv.interpolationDecay = DECAY_MOBILE;
    mv.style.pointerEvents = 'auto';
    mv.style.touchAction = 'none';
    setOrbit(0, BASE_PHI);
  }

  function onTouchStart(event) {
    if (!viewer || !event.touches || event.touches.length !== 1) return;
    var t = event.touches[0];
    clearResetTimer();
    try {
      viewer.interpolationDecay = DECAY_MOBILE;
    } catch (e) {}
    isDragging = true;
    dragMoved = false;
    dragStartX = t.clientX;
    dragStartY = t.clientY;
    dragBaseYaw = orbitYaw;
    dragBasePhi = orbitPhi;
  }

  function onTouchMove(event) {
    if (!isDragging || !viewer || !event.touches || event.touches.length !== 1) return;
    var t = event.touches[0];
    var dx = t.clientX - dragStartX;
    var dy = t.clientY - dragStartY;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      dragMoved = true;
    }
    if (!dragMoved) return;
    event.preventDefault();

    var rect = viewer.getBoundingClientRect();
    /* Full-range sensitivity: ~1 finger-width across logo ≈ 180° yaw */
    var scaleX = rect.width ? 360 / rect.width : 1;
    var scaleY = rect.height ? 180 / rect.height : 1;
    /* Inverted horizontal + vertical */
    var yaw = dragBaseYaw - dx * scaleX;
    var phi = dragBasePhi - dy * scaleY;
    phi = Math.max(PHI_MIN, Math.min(PHI_MAX, phi));
    setOrbit(yaw, phi);
  }

  function onTouchEnd() {
    isDragging = false;
    if (dragMoved) {
      scheduleReset();
    }
  }

  function onWrapClick(event) {
    if (dragMoved) {
      event.preventDefault();
      event.stopPropagation();
      dragMoved = false;
    }
  }

  function initDesktop(mv, signal) {
    function start() {
      bindDesktop(mv);
      window.addEventListener('pointermove', onPointerMove, {
        passive: true,
        signal: signal
      });
    }

    if (mv.loaded) {
      start();
    } else {
      mv.addEventListener('load', start, { once: true, signal: signal });
      window.setTimeout(function () {
        if (!signal.aborted && mv.loaded) start();
      }, 0);
    }
  }

  function initMobile(mv, signal) {
    function start() {
      bindMobile(mv);
      var wrap = mv.closest('.fkx-logo-wrap') || mv.parentElement;
      if (wrap) {
        wrap.style.touchAction = 'none';
        wrap.addEventListener('touchstart', onTouchStart, { passive: true, signal: signal });
        wrap.addEventListener('touchmove', onTouchMove, { passive: false, signal: signal });
        wrap.addEventListener('touchend', onTouchEnd, { passive: true, signal: signal });
        wrap.addEventListener('touchcancel', onTouchEnd, { passive: true, signal: signal });
        wrap.addEventListener('click', onWrapClick, { capture: true, signal: signal });
      }
    }

    if (mv.loaded) {
      start();
    } else {
      mv.addEventListener('load', start, { once: true, signal: signal });
      window.setTimeout(function () {
        if (!signal.aborted && mv.loaded) start();
      }, 0);
    }
  }

  function init() {
    var mv = document.querySelector(SELECTOR);
    if (!mv) return;

    if (controller) {
      controller.abort();
    }
    controller = new AbortController();
    var signal = controller.signal;
    isDragging = false;
    dragMoved = false;

    if (isDesktop()) {
      initDesktop(mv, signal);
    } else {
      initMobile(mv, signal);
    }
  }

  function destroy() {
    clearResetTimer();
    if (controller) {
      controller.abort();
      controller = null;
    }
    if (raf) {
      window.cancelAnimationFrame(raf);
      raf = 0;
    }
    if (viewer) {
      viewer.removeAttribute('auto-rotate');
      try {
        viewer.autoRotate = false;
      } catch (e) {}
    }
    viewer = null;
    hasPending = false;
    isDragging = false;
  }

  window.__fkxGlbLogoTrack = { init: init, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    destroy();
    init();
  });

  window.matchMedia(FINE).addEventListener('change', function () {
    destroy();
    init();
  });
})();
